/**
 * useTimeline — the React binding for the Engineering Timeline.
 *
 * Strict layering: the page calls THIS hook; the hook composes the feature
 * STORES (Goal / Workstream / TeamDocument / Decision / Blocker / Task +
 * TimelineEvent), each of which talks to the api facade. The page never imports
 * a store or the api directly.
 *
 * The timeline itself is a pure client-side DERIVATION ({@link buildTimeline}):
 * the hook loads every relevant collection once (in parallel), hydrates the
 * full goal documents so it can read their milestones, folds them + the optional
 * explicit events into one chronological stream, and re-derives whenever the
 * selected scope (all / goal / workstream / service) changes. Explicit events
 * are the only persisted piece here (manual milestones / notes).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TaskStore } from '../tasks/taskStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { DecisionStore } from '../decisions/decisionStore';
import { BlockerStore } from '../blockers/blockerStore';
import { ServiceStore } from '../services/serviceStore';
import { TimelineEventStore } from './timelineStore';
import { buildTimeline } from './timeline';
import type {
  DerivedTimelineEvent,
  TimelineEvent,
  TimelineEventDraft,
  TimelineEventSummary,
  TimelineScope,
} from './types';
import type { Goal, GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { Task } from '../tasks/types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';
import type { BlockerSummary } from '../blockers/types';
import type { ServiceSummary } from '../services/types';

export interface UseTimelineResult {
  /** The derived, scoped, newest-first timeline. */
  events: DerivedTimelineEvent[];
  scope: TimelineScope;
  setScope: (scope: TimelineScope) => void;
  /** Scope + picker option sources. */
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  services: ServiceSummary[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  tasks: Task[];
  /** id -> title maps for rendering event links. */
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  serviceTitles: Map<string, string>;
  documentTitles: Map<string, string>;
  decisionTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createEvent: (draft: TimelineEventDraft) => Promise<TimelineEvent>;
  updateEvent: (id: string, draft: TimelineEventDraft) => Promise<TimelineEvent | undefined>;
  removeEvent: (id: string) => Promise<void>;
}

export function useTimeline(initialScope: TimelineScope = { type: 'all' }): UseTimelineResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      tasks: new TaskStore(api),
      documents: new TeamDocumentStore(api),
      decisions: new DecisionStore(api),
      blockers: new BlockerStore(api),
      services: new ServiceStore(api),
      events: new TimelineEventStore(api),
    }),
    [api],
  );

  const [fullGoals, setFullGoals] = useState<Goal[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<TeamDocumentSummary[]>([]);
  const [decisions, setDecisions] = useState<DecisionSummary[]>([]);
  const [blockers, setBlockers] = useState<BlockerSummary[]>([]);
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [explicitEvents, setExplicitEvents] = useState<TimelineEventSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [scope, setScope] = useState<TimelineScope>(initialScope);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [goalRows, wsRows, allTasks, docRows, decisionRows, blockerRows, serviceRows, eventRows, identity] =
        await Promise.all([
          stores.goals.listSummaries(),
          stores.workstreams.listSummaries(),
          stores.tasks.listAll(),
          stores.documents.listSummaries(),
          stores.decisions.listSummaries(),
          stores.blockers.listSummaries(),
          stores.services.listSummaries(),
          stores.events.listSummaries(),
          api.identityProvider.getIdentity(),
        ]);

      // Hydrate full goal docs so we can read their milestones (not on the
      // pointer-row summary). Missing/deleted goals are skipped.
      const hydrated = (
        await Promise.all(goalRows.map((g) => stores.goals.get(g.id)))
      ).filter((g): g is Goal => !!g && !g.deleted);

      setGoals(goalRows);
      setFullGoals(hydrated);
      setWorkstreams(wsRows);
      setTasks(allTasks);
      setDocuments(docRows);
      setDecisions(decisionRows);
      setBlockers(blockerRows);
      setServices(serviceRows);
      setExplicitEvents(eventRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the timeline.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const events = useMemo(() => {
    const scopeService =
      scope.type === 'service' && scope.id
        ? services.find((s) => s.id === scope.id)
        : undefined;
    return buildTimeline(
      {
        goals: fullGoals,
        workstreams,
        documents,
        decisions,
        blockers,
        explicitEvents,
        scopeService,
      },
      scope,
    );
  }, [fullGoals, workstreams, documents, decisions, blockers, explicitEvents, services, scope]);

  const mapOf = <T,>(items: T[], id: (t: T) => string, title: (t: T) => string) => {
    const m = new Map<string, string>();
    for (const it of items) m.set(id(it), title(it));
    return m;
  };

  const goalTitles = useMemo(() => mapOf(goals, (g) => g.id, (g) => g.title), [goals]);
  const workstreamTitles = useMemo(
    () => mapOf(workstreams, (w) => w.id, (w) => w.title),
    [workstreams],
  );
  const serviceTitles = useMemo(
    () => mapOf(services, (s) => s.id, (s) => s.name),
    [services],
  );
  const documentTitles = useMemo(
    () => mapOf(documents, (d) => d.id, (d) => d.title),
    [documents],
  );
  const decisionTitles = useMemo(
    () => mapOf(decisions, (d) => d.id, (d) => d.decision),
    [decisions],
  );
  const storyTitles = useMemo(() => mapOf(tasks, (t) => t.id, (t) => t.title), [tasks]);

  const createEvent = useCallback(
    async (draft: TimelineEventDraft) => {
      const saved = await stores.events.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const updateEvent = useCallback(
    async (id: string, draft: TimelineEventDraft) => {
      const existing = await stores.events.get(id);
      if (!existing) return undefined;
      const saved = await stores.events.applyDraft(existing, draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const removeEvent = useCallback(
    async (id: string) => {
      await stores.events.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  return {
    events,
    scope,
    setScope,
    goals,
    workstreams,
    services,
    documents,
    decisions,
    tasks,
    goalTitles,
    workstreamTitles,
    serviceTitles,
    documentTitles,
    decisionTitles,
    storyTitles,
    currentAlias,
    loading,
    error,
    refresh,
    createEvent,
    updateEvent,
    removeEvent,
  };
}
